/**
 * The arithmetic that fills a test's calculated markers, with nothing around it.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Every one of these formulas lived as a method on the entry form, fired from
 * `handleOnBlur`. That was fine while a blurred input was the only way a test
 * could ever be built. It is not any more: results pushed in from an outside lab
 * arrive at a Lambda, where there is no form, no blur and no React, and a test
 * written straight to DynamoDB from there would carry a blank globulin, blank
 * ratios and a blank transferrin saturation forever.
 *
 * So the arithmetic moves here, as plain functions over plain numbers, and both
 * callers share it. What did NOT move is everything the form does AROUND the
 * arithmetic: which field a blur recomputes, whether a stale ratio is cleared,
 * and which values are the calculation's to overwrite. Those are decisions about
 * an editing session and they stay in the component. See `deriveMissing` for the
 * server's much simpler rule.
 *
 * EVERY FUNCTION HERE IS TOTAL AND PURE
 * -------------------------------------
 * Each one answers with a number or with `undefined`, and `undefined` always
 * means the same thing: the inputs do not support this marker, so it has no
 * value. Never zero, never NaN, never a guess. A marker the lab did not report
 * has to stay empty, because zero is a real result that reads as optimal on
 * several of these and would quietly move a gauge.
 *
 * ROUNDING IS PART OF THE FORMULA
 * -------------------------------
 * Each function rounds to the precision the entry form has always rounded that
 * marker to, rather than leaving it to the caller. Two callers rounding for
 * themselves is two chances to disagree, and a test pushed by a lab would then
 * differ in the second decimal from the same test typed by hand.
 */
import { MathUtils } from '../utils/MathUtils';

/**
 * A field's value as a number, or NaN when the field carries nothing.
 *
 * `Number('')` is 0, not NaN, and that is the whole trap. Every calculation
 * below guards its inputs with isNaN, so an empty field would pass that guard as
 * a zero and then take part in the arithmetic: divided into, it produces
 * Infinity and renders as "NaN"; subtracted, it produces a plausible wrong
 * number instead. A field the lab did not report has to arrive as NaN so the
 * guard actually stops it.
 *
 * Lifted from the entry form's `numericField`, which now calls this.
 */
export function numericValue(value: any): number {
  return Number(value === '' || value == null ? undefined : value);
}

/** Did anything at all get reported for this field? */
export function isMeasured(value: any): boolean {
  return !isNaN(numericValue(value));
}

/** Globulin, total = protein, total − albumin. */
export function deriveGlobulin(protein: any, albumin: any): number | undefined {
  const p = numericValue(protein);
  const a = numericValue(albumin);
  if (isNaN(p) || isNaN(a)) return undefined;
  return MathUtils.round(p - a, 2);
}

/**
 * VLDL cholesterol = total cholesterol − HDL-C − LDL-C.
 *
 * NAMED FORMULA, and it is the one the labs use. Every report that prints this
 * row calls it CALCULATED (`VLDL Cholesterol Cal`, `VLDL Calculated`), and on
 * the corpus report that prints all four components it reproduces the lab's own
 * number exactly: 197 − 67 − 120 = 10, where the lab printed 10. The
 * Friedewald alternative, triglycerides / 5, gives 10.6 on that same report and
 * is therefore NOT what these labs are doing.
 *
 * ⚠ ONLY REACHED WHEN THE LAB DID NOT PRINT IT. 49 of the corpus reports do
 * print it, across six labs, and `vLDL` carries aliases for all of their
 * spellings, so the measured row wins and this fills the gap. That ordering is
 * `deriveMissing`'s own rule (`fill` never overwrites a measured value), and it
 * matters here because the lab's LDL-C may itself be the NIH/Sampson
 * calculation rather than the Friedewald one, and reproducing the subtraction
 * off a different LDL would not give the same answer the report shows.
 *
 * Returns undefined rather than a negative number: cholesterol − HDL − LDL can
 * go below zero on a panel whose three components came from different draws,
 * and a negative VLDL is not a result to store.
 */
export function deriveVldl(
  cholesterol: any,
  hdl: any,
  ldl: any,
): number | undefined {
  const total = numericValue(cholesterol);
  const h = numericValue(hdl);
  const l = numericValue(ldl);
  if (isNaN(total) || isNaN(h) || isNaN(l)) return undefined;
  const vldl = MathUtils.round(total - h - l, 2);
  return vldl >= 0 ? vldl : undefined;
}

/**
 * MCV = hematocrit × 10 / RBC.
 *
 * Worth knowing what this is: a modern hematology analyzer MEASURES MCV directly
 * by impedance and DERIVES the hematocrit from MCV × RBC, so where a report
 * prints an MCV that number is the measured one and this is a back-calculation
 * of something the analyzer never measured. This fills a gap. It does not
 * overrule a lab. Both callers are built that way — see `deriveMissing` here and
 * `indexIsOpenToDerivation` in bloodwork-entry.tsx.
 */
export function deriveMeanCorpuscularVolume(
  hematocrit: any,
  redBloodCells: any,
): number | undefined {
  const hct = numericValue(hematocrit);
  const rbc = numericValue(redBloodCells);
  if (isNaN(hct) || isNaN(rbc) || rbc === 0) return undefined;
  return MathUtils.round((hct * 10) / rbc, 2);
}

/** MCH = hemoglobin × 10 / RBC. Measured by the analyzer too; see MCV above. */
export function deriveMeanCorpuscularHGB(
  hemoglobin: any,
  redBloodCells: any,
): number | undefined {
  const hgb = numericValue(hemoglobin);
  const rbc = numericValue(redBloodCells);
  if (isNaN(hgb) || isNaN(rbc) || rbc === 0) return undefined;
  return MathUtils.round((hgb * 10) / rbc, 2);
}

/** MCHC = hemoglobin × 100 / hematocrit. Measured by the analyzer too. */
export function deriveMchConcentration(
  hemoglobin: any,
  hematocrit: any,
): number | undefined {
  const hgb = numericValue(hemoglobin);
  const hct = numericValue(hematocrit);
  if (isNaN(hgb) || isNaN(hct) || hct === 0) return undefined;
  return MathUtils.round((hgb * 100) / hct, 2);
}

/** Direct bilirubin = total − indirect. */
export function deriveBilirubinDirect(
  total: any,
  indirect: any,
): number | undefined {
  const t = numericValue(total);
  const i = numericValue(indirect);
  if (isNaN(t) || isNaN(i)) return undefined;
  return MathUtils.round(t - i, 2);
}

/** Indirect bilirubin = total − direct. */
export function deriveBilirubinIndirect(
  total: any,
  direct: any,
): number | undefined {
  const t = numericValue(total);
  const d = numericValue(direct);
  if (isNaN(t) || isNaN(d)) return undefined;
  return MathUtils.round(t - d, 2);
}

/**
 * Transferrin saturation = iron / TIBC × 100.
 *
 * A report giving iron and no TIBC is ordinary rather than a mistake: the
 * Hospital Joya panel prints Hierro Serico and Transferrina and never a TIBC.
 * Saturation is simply unknown there and has to stay blank rather than be
 * manufactured.
 */
export function deriveSaturation(iron: any, tibc: any): number | undefined {
  const fe = numericValue(iron);
  const capacity = numericValue(tibc);
  if (isNaN(fe) || isNaN(capacity) || capacity === 0) return undefined;
  return MathUtils.round((fe / capacity) * 100, 1);
}

/** Bioavailable testosterone as a percentage of total. */
export function deriveTestosteroneBioavailablePercent(
  bioavailable: any,
  total: any,
): number | undefined {
  const bio = numericValue(bioavailable);
  const tot = numericValue(total);
  if (isNaN(bio) || isNaN(tot) || tot === 0) return undefined;
  return MathUtils.round((bio / tot) * 100, 1);
}

/**
 * One marker divided by another, to two places.
 *
 * `undefined` for a missing component rather than a number, which is the whole
 * point: a blank numerator yields 0, and 0 reads as OPTIMAL on TG/HDL, one of
 * the three primary Glucose markers. A ratio the lab never reported has to stay
 * empty.
 */
export function deriveRatio(
  numerator: any,
  denominator: any,
): number | undefined {
  if (!isMeasured(numerator) || !isMeasured(denominator)) return undefined;
  const d = numericValue(denominator);
  if (d === 0) return undefined;
  const ratio = MathUtils.round(numericValue(numerator) / d, 2);
  return isNaN(ratio) ? undefined : ratio;
}

/**
 * Every ratio the app calculates, as numerator / denominator / where it lands.
 *
 * A list rather than a run of calls so both callers iterate the same one and a
 * ratio added here reaches the form and the server together. The ORDER matters:
 * the A/G ratio's denominator is globulin, which is itself derived, so anything
 * walking this list has to have filled globulin first. `deriveMissing` does.
 */
export const DERIVED_RATIOS: {
  field: string;
  numerator: string;
  denominator: string;
}[] = [
  { field: 'bunCreatinineRatio', numerator: 'bun', denominator: 'creatinine' },
  { field: 'albuminGlobulinRatio', numerator: 'albumin', denominator: 'globulin' },
  { field: 'cholesterolHDLRatio', numerator: 'cholesterol', denominator: 'hdl' },
  { field: 'triglyceridesHDLRatio', numerator: 'triglycerides', denominator: 'hdl' },
  { field: 'apoBapoA1Ratio', numerator: 'apolipoproteinB', denominator: 'apolipoproteinA1' },
];

/**
 * EVERY ratio a given marker takes part in, as numerator or as denominator.
 *
 * Every one, which is the whole reason this is a filter and not a lookup: HDL is
 * a component of BOTH Cholesterol/HDL and Triglycerides/HDL. A rule that stopped
 * at the first match left the second stale, and that bit TG/HDL — it repopulated
 * only when Triglycerides was blurred, so entering Triglycerides before HDL left
 * it blank. TG/HDL is one of the three primary Glucose markers, so a blank there
 * silently shifts the Glucose gauge and the pattern the report prints.
 *
 * Here rather than in the entry form because it is the form's recalculation rule
 * AND the answer to "what else does this marker change", which the ingest path
 * will want the moment a correction comes in for one marker of a saved test.
 */
export function ratiosInvolving(field: string): typeof DERIVED_RATIOS {
  return DERIVED_RATIOS.filter(
    ratio => ratio.numerator === field || ratio.denominator === field,
  );
}

/** What eGFR needs besides creatinine, none of which is on the test itself. */
export type EgfrContext = {
  /** Age in years AT COLLECTION, not today. */
  ageYears?: number | null;
  isFemale?: boolean;
  /** Only read under 18, where the pediatric equation is height-based. */
  heightCm?: number | null;
};

/**
 * eGFR from creatinine: CKD-EPI 2021 for adults, bedside Schwartz under 18.
 *
 * CKD-EPI 2021 is the race-free equation, which is what Labcorp and Quest both
 * report now, so a value calculated here and a value printed on a current report
 * are the same equation rather than two that happen to share a name.
 *
 * Nothing calls this to fill an empty field today. The entry form calls it in
 * one place only: to recover a real number where the lab printed ">90" or ">60"
 * and stopped. Whether the ingest path should calculate it from scratch when the
 * lab sends none is an open question — see `deriveMissing`, which leaves it off.
 */
export function deriveEgfr(
  creatinine: any,
  context: EgfrContext,
): number | undefined {
  const cr = numericValue(creatinine);
  if (isNaN(cr) || cr === 0) return undefined;

  /*
   * `!(age > 0)` rather than `age <= 0`, so a NaN age is refused too. The entry
   * form's own guard was `!age`, which catches NaN; `age <= 0` does not, and a
   * NaN reaching the equation below comes back out as a NaN eGFR on the test.
   */
  const age = context.ageYears;
  if (age == null || !(age > 0)) return undefined;

  if (age < 18) {
    const heightCm = context.heightCm;
    if (!heightCm) return undefined;
    return Math.round(((0.413 * heightCm) / cr) * 10) / 10;
  }

  const isFemale = !!context.isFemale;
  const kappa = isFemale ? 0.7 : 0.9;
  const alpha = isFemale ? -0.241 : -0.302;
  const sexFactor = isFemale ? 1.012 : 1.0;
  const ratio = cr / kappa;
  return (
    Math.round(
      142 *
        Math.pow(Math.min(ratio, 1), alpha) *
        Math.pow(Math.max(ratio, 1), -1.2) *
        Math.pow(0.9938, age) *
        sexFactor *
        10,
    ) / 10
  );
}

/**
 * How a lab reported the white cell differential, matching the stored enum in
 * WhiteBloodCellDifferentialUnitType. Repeated as plain numbers because that
 * enum lives behind the models barrel and this module stays leaf-clean.
 */
export const DIFFERENTIAL_PERCENT = 1;
export const DIFFERENTIAL_ABSOLUTE_KCELLS = 2;
export const DIFFERENTIAL_ABSOLUTE_CELLS = 3;

/** The five differential markers, in the order the form and the report print. */
export const DIFFERENTIAL_FIELDS = [
  'neutrophils',
  'lymphocytes',
  'monocytes',
  'eosinophils',
  'basophils',
];

/**
 * One differential value as the DECIMAL FRACTION the app stores.
 *
 * 58.4% is stored as 0.584. A lab pushing results reports the differential the
 * way its analyzer does, which is one of three ways, and a report in absolute
 * counts cannot be converted without the white cell count to divide by — so a
 * missing WBC returns the value untouched rather than a wrong fraction.
 *
 * DELIBERATELY A SECOND IMPLEMENTATION of Bloodwork.convertToDecimal and
 * Bloodwork.convertToDecimalFromAbsolute, which is the opposite of what the rest
 * of this file is for, so it needs its reason. Those two are methods on a class
 * whose own state they read (isWhiteBloodCellsDefined) and whose null handling
 * the entry form depends on; making them delegate would be a refactor of working
 * code for three lines of arithmetic. The safeguard is not a shared function but
 * a test: BloodworkDerivation.test.ts runs both over the same inputs and fails
 * if they ever disagree. Change one and that test tells you about the other.
 */
export function differentialToDecimal(
  value: any,
  units: number,
  whiteBloodCells?: any,
): number | undefined {
  const raw = numericValue(value);
  if (isNaN(raw)) return undefined;

  if (units === DIFFERENTIAL_PERCENT) {
    return MathUtils.round(raw / 100, 4);
  }

  const wbc = numericValue(whiteBloodCells);
  if (isNaN(wbc) || wbc === 0) return raw;

  const multiplier = units === DIFFERENTIAL_ABSOLUTE_CELLS ? 1000 : 1;
  return MathUtils.round(raw / (wbc * multiplier), 4);
}

/** A bag of marker values, which is every shape a test takes on its way in. */
export type MarkerValues = { [field: string]: any };

export type DeriveMissingOptions = {
  /** What eGFR needs. Without it, eGFR is not attempted at all. */
  egfr?: EgfrContext;
  /**
   * Calculate eGFR where the lab sent none.
   *
   * OFF by default, deliberately, because nothing in the app does this today:
   * the entry form calculates eGFR only to replace a printed ">90". Turning it
   * on would put a number on tests that have never carried one, which is a
   * product decision rather than a refactor, so it waits to be asked for.
   */
  calculateEgfr?: boolean;
};

/**
 * Fill in the markers a set of results does not carry, and NOTHING else.
 *
 * The server's rule, and it is deliberately simpler than the form's: a field
 * that already holds a value is never touched, whoever put it there. There is no
 * provenance to track because there is no editing session — one payload arrives,
 * it is complete or it is not, and what the lab measured always wins over what
 * this file can work out.
 *
 * Returns ONLY the fields it filled, so the caller can merge, log or count them.
 * An empty object means the results needed nothing.
 *
 * Order is dependency order and it matters in one place: globulin is derived,
 * and it is the A/G ratio's denominator, so the ratios run last and see it.
 */
export function deriveMissing(
  values: MarkerValues,
  options: DeriveMissingOptions = {},
): MarkerValues {
  const filled: MarkerValues = {};
  const at = (field: string) =>
    filled[field] !== undefined ? filled[field] : values[field];
  const fill = (field: string, value: number | undefined) => {
    if (value === undefined) return;
    if (isMeasured(at(field))) return;
    filled[field] = value;
  };

  fill('globulin', deriveGlobulin(at('protein'), at('albumin')));

  fill(
    'meanCorpuscularVolume',
    deriveMeanCorpuscularVolume(at('hematocrit'), at('redBloodCells')),
  );
  fill(
    'meanCorpuscularHGB',
    deriveMeanCorpuscularHGB(at('hemoglobin'), at('redBloodCells')),
  );
  fill(
    'mchConcentration',
    deriveMchConcentration(at('hemoglobin'), at('hematocrit')),
  );

  // Whichever of the two is missing, from the total and the other one. A panel
  // that ran neither, or ran both, needs nothing here.
  if (isMeasured(at('bilirubin'))) {
    fill(
      'bilirubinIndirect',
      deriveBilirubinIndirect(at('bilirubin'), at('bilirubinDirect')),
    );
    fill(
      'bilirubinDirect',
      deriveBilirubinDirect(at('bilirubin'), at('bilirubinIndirect')),
    );
  }

  fill('saturation', deriveSaturation(at('iron'), at('tibc')));

  // Before the ratios, which do not read it, and after nothing: its three
  // components are all measured rows.
  fill('vLDL', deriveVldl(at('cholesterol'), at('hdl'), at('ldl')));

  fill(
    'testosteroneBioavailablePercent',
    deriveTestosteroneBioavailablePercent(
      at('testosteroneBioavailable'),
      at('testosteroneTotal'),
    ),
  );

  if (options.calculateEgfr && options.egfr) {
    fill('eGFR', deriveEgfr(at('creatinine'), options.egfr));
  }

  // Last, so the A/G ratio sees a globulin this same pass derived.
  DERIVED_RATIOS.forEach(({ field, numerator, denominator }) => {
    fill(field, deriveRatio(at(numerator), at(denominator)));
  });

  return filled;
}
